'use client'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ChromaText } from '@/components/ui/chroma-text'

import { Breadcrumbs } from '@/components/breadcrumbs'
import { EASE, HAIRLINE, useReveal } from '@/components/page-kit'
import { LMS, LMS_MODULES } from './data'

/* The hero closes on the six modules numbered like a course catalogue —
   ordinal above the entry — rather than UDC's name-and-descriptor cells.

   The h1 appends the acronym and the audience to the authored title: "LMS" is
   the word this page is searched by and the full title never contains it. Both
   additions are already in the page's own metadata. */

export function LmsHero() {
  const band = useReveal<HTMLDivElement>(0.25)

  return (
    <section
      className={cn(
        'relative overflow-hidden hero-adaptive-bg',
        'mt-[calc(var(--header-h)*-1)]'
      )}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          backgroundImage: 'var(--circuit-bg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          WebkitMaskImage:
            'radial-gradient(ellipse 110% 68% at 50% 26%, #000 18%, rgba(0,0,0,0.45) 60%, transparent 94%)',
          maskImage:
            'radial-gradient(ellipse 110% 68% at 50% 26%, #000 18%, rgba(0,0,0,0.45) 60%, transparent 94%)',
        }}
      />

      <div className="container relative z-10 mx-auto">
        <div className="flex min-h-[80svh] flex-col justify-center pb-14 pt-[calc(var(--header-h)+4rem)] sm:pb-20">
          <Breadcrumbs
            items={[
              { name: 'Home', href: '/' },
              { name: 'Products', href: '/products' },
              { name: LMS.title, href: `/products/${LMS.slug}` },
            ]}
          />

          <h1
            className={cn(
              'font-heading mt-6 max-w-4xl font-normal tracking-tight text-foreground',
              'text-[32px]/[1.14] sm:text-[44px]/[1.14] md:text-5xl/[1.12] lg:text-[60px]/[1.1]'
            )}
          >
            <ChromaText>{`${LMS.title} (${LMS.shortTitle}) for colleges and schools`}</ChromaText>
          </h1>

          <p className="mt-5 max-w-2xl text-lg font-medium leading-snug text-emerald-600 sm:text-xl md:text-2xl dark:text-primary-active">
            {LMS.tagline}
          </p>

          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#2B3A46] sm:text-lg dark:text-[#DEE9EE]/90">
            {LMS.description}
          </p>

          <div className="mt-9 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
            <Button asChild size="xl">
              <a href="/schedule-meeting">Request a demo</a>
            </Button>

            <Button variant="outline" asChild size="xl">
              <a href="#included">See what's included</a>
            </Button>
          </div>
        </div>
      </div>

      {/* ── The contents ───────────────────────────────────────────────────
          Rule full-bleed, cells in the container so the first entry lines up
          with the headline above. */}
      <div ref={band.ref} className={cn('relative z-10 border-y', HAIRLINE)}>
        <div className="container mx-auto">
          {/* gap-px over a border-coloured backing paints rules between cells
              only, so the dividers stay exact at every breakpoint without
              nth-child padding gymnastics. */}
          <ol className="grid grid-cols-2 gap-px bg-border/70 sm:grid-cols-3 lg:grid-cols-6">
            {LMS_MODULES.map((m, i) => (
              <li
                key={m.name}
                className="bg-background py-5 pl-4 pr-4 first:pl-0 sm:py-6 sm:pl-5 sm:pr-5"
                style={{
                  opacity: band.shown ? 1 : 0,
                  transform: band.shown ? 'none' : 'translateY(8px)',
                  transition: `opacity 460ms ${EASE} ${i * 70}ms, transform 460ms ${EASE} ${i * 70}ms`,
                }}
              >
                <span className="text-[10px] font-semibold tabular-nums text-muted-foreground/60">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className="font-heading mt-1.5 text-sm font-normal leading-tight text-foreground sm:text-base">
                  {m.name}
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  )
}
